import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import * as authApi from "@/api/auth";
import { getProfile, type Profile } from "@/api/profile";
import { tokenStore } from "@/api/token-store";
import { progressStore } from "@/practice/progress-store";
import i18n from "@/i18n";
import { UI_LANGUAGE_CODES } from "@/i18n/languages";
import type { LoginPayload, RegisterPayload, User } from "@/api/types";

interface AuthContextValue {
  user: User | null;
  status: "loading" | "authenticated" | "unauthenticated";
  /** True when authenticated but the account hasn't completed the guided
   *  onboarding (fresh account with an empty profile). Gates `/app`. */
  needsOnboarding: boolean;
  login: (payload: LoginPayload) => Promise<void>;
  register: (payload: RegisterPayload) => Promise<void>;
  logout: () => Promise<void>;
  refreshMe: () => Promise<void>;
  /** Mark onboarding done (finished or skipped) so the gate stops redirecting. */
  markOnboarded: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

/** Sticky per-device flag — the web analogue of iOS `isOnboardingComplete`. Set
 *  when the user finishes or skips onboarding so we don't redirect them again. */
const ONBOARDED_KEY = "edupause.onboarded";

/** Apply the user's saved UI language (best-effort) and decide whether they still
 *  need onboarding. A 404 (no profile row) → fresh account → needs onboarding; an
 *  existing-but-empty profile (e.g. an OAuth signup) also counts. A populated
 *  profile (e.g. an existing iOS user) does not. */
async function hydrateProfile(): Promise<{ needsOnboarding: boolean }> {
  if (localStorage.getItem(ONBOARDED_KEY) === "1") {
    // Still apply language, but we already know onboarding is done.
    void applyLanguage();
    return { needsOnboarding: false };
  }
  let profile: Profile | null = null;
  try {
    profile = await getProfile();
  } catch {
    profile = null; // 404 (no profile) or transient — treat as fresh
  }
  if (profile) applyLanguageFrom(profile);
  const empty =
    !profile ||
    (!profile.learning_status &&
      !profile.difficulty_preference &&
      (!profile.subject_interests || profile.subject_interests.length === 0));
  return { needsOnboarding: empty };
}

function applyLanguageFrom(profile: Profile): void {
  const code = profile.preferred_language;
  if (code && UI_LANGUAGE_CODES.includes(code) && code !== i18n.resolvedLanguage) {
    void i18n.changeLanguage(code);
  }
}

async function applyLanguage(): Promise<void> {
  try {
    applyLanguageFrom(await getProfile());
  } catch {
    /* keep the detector's choice */
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [needsOnboarding, setNeedsOnboarding] = useState(false);
  const [status, setStatus] = useState<AuthContextValue["status"]>(
    tokenStore.isAuthenticated ? "loading" : "unauthenticated",
  );

  const refreshMe = useCallback(async () => {
    if (!tokenStore.isAuthenticated) {
      progressStore.reset();
      setUser(null);
      setNeedsOnboarding(false);
      setStatus("unauthenticated");
      return;
    }
    try {
      const me = await authApi.getMe();
      // Force a fresh fetch of the progress blob for the now-current user, and
      // AWAIT it so the authenticated UI only composes/flushes after the blob is
      // loaded (an un-awaited hydrate let Train read an empty blob and a flush
      // overwrite the server before the real progress arrived). The store is a
      // singleton that outlives a logout; forcing re-fetches the now-current
      // user's blob instead of early-returning on the stale `hydrated` flag.
      await progressStore.hydrate(true);
      // Apply the saved UI language + decide whether onboarding is still needed
      // (shared with iOS via the same profile fields). AWAITed so the gate has a
      // verdict before the authenticated UI renders (avoids an onboarding flash).
      const { needsOnboarding: needs } = await hydrateProfile();
      setNeedsOnboarding(needs);
      setUser(me);
      setStatus("authenticated");
    } catch {
      tokenStore.clear();
      progressStore.reset();
      setUser(null);
      setNeedsOnboarding(false);
      setStatus("unauthenticated");
    }
  }, []);

  const markOnboarded = useCallback(() => {
    localStorage.setItem(ONBOARDED_KEY, "1");
    setNeedsOnboarding(false);
  }, []);

  // Hydrate the session on first load (silent refresh handled by the client).
  useEffect(() => {
    void refreshMe();
  }, [refreshMe]);

  // Keep context in sync if tokens are cleared elsewhere (e.g. a hard 401).
  useEffect(() => {
    return tokenStore.subscribe(() => {
      if (!tokenStore.isAuthenticated) {
        progressStore.reset();
        setUser(null);
        setNeedsOnboarding(false);
        setStatus("unauthenticated");
      }
    });
  }, []);

  // Persist the tail of an active session when the tab is hidden/closed —
  // the progress store flushes on a 5s debounce, so without this an answer
  // made just before leaving never reaches PUT /learner/progress.
  useEffect(() => {
    const onHidden = () => {
      if (document.visibilityState === "hidden") void progressStore.flush();
    };
    document.addEventListener("visibilitychange", onHidden);
    window.addEventListener("pagehide", onHidden);
    return () => {
      document.removeEventListener("visibilitychange", onHidden);
      window.removeEventListener("pagehide", onHidden);
    };
  }, []);

  const login = useCallback(
    async (payload: LoginPayload) => {
      await authApi.login(payload);
      await refreshMe();
    },
    [refreshMe],
  );

  const register = useCallback(
    async (payload: RegisterPayload) => {
      await authApi.register(payload);
      await refreshMe();
    },
    [refreshMe],
  );

  const logout = useCallback(async () => {
    // Persist any unflushed progress before tearing down, then drop the
    // singleton's state so the next login starts from a clean, re-fetched blob.
    await progressStore.flush();
    await authApi.logout();
    progressStore.reset();
    // Per-device flag — clear it so a different user signing in on this device
    // still gets their own onboarding verdict.
    localStorage.removeItem(ONBOARDED_KEY);
    setUser(null);
    setNeedsOnboarding(false);
    setStatus("unauthenticated");
  }, []);

  const value = useMemo(
    () => ({ user, status, needsOnboarding, login, register, logout, refreshMe, markOnboarded }),
    [user, status, needsOnboarding, login, register, logout, refreshMe, markOnboarded],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within <AuthProvider>");
  return ctx;
}
